// 서버 복구(navigator.onLine 무관 502 해제 등) 감지 시, viewer 에 표시 중이며 본문이
// 미로딩(emailInfo.body == null)인 메일의 body 를 자동 재요청해 빈 화면을 자동 복구한다.
//
// OFM-214 에서 구 ThreadView(단일 컴포넌트)에 있던 serverRecovery 로직을, ngwEditor
// 마이그레이션으로 분리된 IFrameViewer / InnerViewer 양쪽에서 공유하도록 추출.
// 두 뷰어의 유일한 차이는 viewer window 접근 방식(iframe.contentWindow vs host window)
// 이므로 host 가 접근자(getViewerWindow 등)를 주입하고 나머지 로직은 공통화한다.
//
// notifyServerRecovery 는 정상 Email/get 응답마다 발화하므로 debounce(500ms)로 중복
// 호출을 억제한다.

import { jmapReqQueue } from 'pages/main/data/JMAPMailbox'

type ViewerWindowLike = (Window & { webObj?: any }) | null

export interface ServerRecoveryHost {
  /** 현재 마운트 상태. unmount 후 stale viewer 접근 방지용. */
  getMounted: () => boolean
  /** emailId → emailInfo 맵. body 미로딩 메일을 추려 재요청한다. */
  getEmailInfoMap: () => Record<string, any>
  /** viewer window (IFrameViewer=iframe.contentWindow, InnerViewer=host window). */
  getViewerWindow: () => ViewerWindowLike
}

export class ServerRecoveryController {
  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  private readonly host: ServerRecoveryHost

  constructor(host: ServerRecoveryHost) {
    this.host = host
  }

  register = () => {
    jmapReqQueue.addServerRecoveryListener(this.handleServerRecovery)
  }

  unregister = () => {
    jmapReqQueue.removeServerRecoveryListener(this.handleServerRecovery)
    if (this.debounceTimer != null) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }

  private handleServerRecovery = () => {
    if (this.debounceTimer != null) {
      return
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null
      this.retryMissingBodies()
    }, 500)
  }

  private retryMissingBodies = () => {
    const { host } = this
    if (host.getMounted() !== true) {
      return
    }
    const viewerWindow = host.getViewerWindow()
    if (viewerWindow == null || viewerWindow.webObj == null) {
      return
    }
    const emailInfoMap = host.getEmailInfoMap()
    if (emailInfoMap == null) {
      return
    }
    Object.keys(emailInfoMap).forEach((emailId) => {
      const emailInfo = emailInfoMap[emailId]
      if (emailInfo == null) {
        return
      }
      // body 가 이미 캐시됐으면(성공적으로 로딩됨) 재요청 불필요.
      if (emailInfo.body != null) {
        return
      }
      emailInfo.api
        .getBody()
        .then((bodyData: Record<string, unknown> | null) => {
          // thread 전환으로 emailInfoMap 이 바뀐 뒤 응답이 도착하면 무시.
          if (host.getEmailInfoMap()[emailId] == null) {
            return
          }
          if (bodyData == null) {
            return
          }
          const vw = host.getViewerWindow()
          if (vw == null || vw.webObj == null) {
            return
          }
          const changedData = JSON.stringify({ body: bodyData })
          vw.webObj.notifyEmailChanged(emailId, changedData)
        })
        .catch(() => {
          // 아직 서버가 복구되지 않았을 수 있으므로 조용히 무시.
          // 다음 notifyServerRecovery 에서 재시도된다.
        })
    })
  }
}
